/**
 * Infrastructure Map API service
 *
 * Provides functions for managing canvas states, style settings, and layout calculation
 * for the infrastructure map visualisation.
 */

import { apiClient } from './api'
import type {
  MapStyle,
  CanvasStateOutput,
  CanvasStatesResponse,
  UpdateCanvasStateInput,
  BulkUpdateCanvasStateInput,
  BulkUpdateResponse,
  MapStyleSettingsOutput,
  UpdateMapStyleSettingsInput,
  CalculateLayoutInput,
  LayoutCalculationResult,
} from '../types/infrastructure-map'

// =============================================================================
// CANVAS STATE API
// =============================================================================

/**
 * List all canvas states for a case and map style
 */
export async function listCanvasStates(
  caseId: string,
  mapStyle: MapStyle
): Promise<CanvasStatesResponse> {
  return apiClient.get<CanvasStatesResponse>(
    `/cases/${caseId}/infrastructure/map/canvas-states?mapStyle=${mapStyle}`
  )
}

export interface UpdateCanvasStateArgs {
  caseId: string
  endpointId: string
  mapStyle: MapStyle
  input: UpdateCanvasStateInput
}

/**
 * Update canvas state for a single endpoint
 */
export async function updateCanvasState(args: UpdateCanvasStateArgs): Promise<CanvasStateOutput> {
  const { caseId, endpointId, mapStyle, input } = args
  return apiClient.patch<CanvasStateOutput>(
    `/cases/${caseId}/infrastructure/map/canvas-states/${endpointId}?mapStyle=${mapStyle}`,
    input
  )
}

/**
 * Bulk update canvas states (used after layout calculation)
 */
export async function bulkUpdateCanvasStates(
  caseId: string,
  mapStyle: MapStyle,
  input: BulkUpdateCanvasStateInput
): Promise<BulkUpdateResponse> {
  return apiClient.put<BulkUpdateResponse>(
    `/cases/${caseId}/infrastructure/map/canvas-states/bulk?mapStyle=${mapStyle}`,
    input
  )
}

/**
 * Delete canvas state for an endpoint
 */
export async function deleteCanvasState(
  caseId: string,
  endpointId: string,
  mapStyle: MapStyle
): Promise<void> {
  await apiClient.delete(
    `/cases/${caseId}/infrastructure/map/canvas-states/${endpointId}?mapStyle=${mapStyle}`
  )
}

/**
 * Clear all canvas states for a case and map style
 */
export async function clearCanvasStates(caseId: string, mapStyle: MapStyle): Promise<number> {
  return apiClient.delete<number>(
    `/cases/${caseId}/infrastructure/map/canvas-states?mapStyle=${mapStyle}`
  )
}

// =============================================================================
// MAP STYLE SETTINGS API
// =============================================================================

/**
 * Get map style settings for a case
 */
export async function getStyleSettings(
  caseId: string,
  mapStyle: MapStyle
): Promise<MapStyleSettingsOutput> {
  return apiClient.get<MapStyleSettingsOutput>(
    `/cases/${caseId}/infrastructure/map/style-settings?mapStyle=${mapStyle}`
  )
}

/**
 * Update map style settings
 */
export async function updateStyleSettings(
  caseId: string,
  mapStyle: MapStyle,
  input: UpdateMapStyleSettingsInput
): Promise<MapStyleSettingsOutput> {
  return apiClient.patch<MapStyleSettingsOutput>(
    `/cases/${caseId}/infrastructure/map/style-settings?mapStyle=${mapStyle}`,
    input
  )
}

// =============================================================================
// LAYOUT CALCULATION API
// =============================================================================

/**
 * Calculate layout for endpoints
 */
export async function calculateLayout(
  caseId: string,
  input: CalculateLayoutInput
): Promise<LayoutCalculationResult> {
  return apiClient.post<LayoutCalculationResult>(
    `/cases/${caseId}/infrastructure/map/calculate-layout`,
    input
  )
}

// =============================================================================
// COMBINED OPERATIONS
// =============================================================================

/**
 * Calculate layout and apply it to canvas states in one operation
 */
export async function calculateAndApplyLayout(
  caseId: string,
  input: CalculateLayoutInput
): Promise<{
  layout: LayoutCalculationResult
  updated: number
}> {
  // First calculate the layout
  const layout = await calculateLayout(caseId, input)

  // If there are nodes to position, bulk update the canvas states
  if (layout.nodes.length > 0) {
    const bulkInput: BulkUpdateCanvasStateInput = {
      states: layout.nodes.map((node) => ({
        endpointId: node.endpointId,
        positionX: node.positionX,
        positionY: node.positionY,
        tileSize: node.tileSize,
      })),
    }

    const updateResult = await bulkUpdateCanvasStates(caseId, input.mapStyle, bulkInput)

    return {
      layout,
      updated: updateResult.updated,
    }
  }

  return {
    layout,
    updated: 0,
  }
}
